import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  TextField,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Link,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import styles from "./entityBasedKyc.module.css";
import { useNavigate } from "react-router-dom";
import { useAllProduct } from "../../api-context/commonApiContext";
import Notification from "../../Notification/Notification";
import TermsAndConditions from "../../TermsAndCondition/TermsAndConditions";
import config from "../../../config/config";
import { CircularProgress } from "@mui/material";

const radius12 = { "& .MuiOutlinedInput-root": { borderRadius: "12px" } };

const SHOW_CIN_VERIFY = new Set([
  "Private Limited (Pvt)",
  "Public Ltd",
  "One Person Company (OPC)",
  "Section 8 Company",
]);
const DISABLE_CIN_INPUT = new Set([
  "Proprietorship",
  "Partnership Firm",
  "Trust",
  "Others",
]);

// Normalize names for comparison (ignore case/punctuation/spaces)
const normalizeName = (s = "") =>
  s
    .replace(/[^\w\s]/g, "")
    .replace(/\s+/g, "")
    .trim()
    .toUpperCase();

const EntityBasedKyc = () => {
  const {
    companyId,
    setCompanyId,
    setBasicKyc,
    entityType,
    setEntityType,
    basicKyc,
    name,
    phoneNo,
    email,
    setName,
    setPhoneNo,
    affiliate_code,
    setAffiliate_code,
    setEmail,
  } = useAllProduct();
  const navigate = useNavigate();

  const [cinModalOpen, setCinModalOpen] = useState(false);
  const [pan, setPan] = useState("");
  const [cin, setCin] = useState("");
  const [companyNamePAN, setCompanyNamePAN] = useState("");
  const [companyNameCIN, setCompanyNameCIN] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyType, setCompanyType] = useState("");

  const [companyTypeError, setCompanyTypeError] = useState("");
  const [registeredAddress, setRegisteredAddress] = useState("");
  const [registeredAddressErr, setRegisteredAddressErr] = useState("");
  const [panVerified, setPanVerified] = useState(false);
  const [cinVerified, setCinVerified] = useState(false);
  const panNameRef = useRef("");
  const [panStatus, setPanStatus] = useState({ msg: "", type: "info" });
  const [cinStatus, setCinStatus] = useState({ msg: "", type: "info" });
  const [agreed, setAgreed] = useState(false);
  const [modal, setModal] = useState(false);
  const [successNoti, setSuccessNoti] = useState(false);
  const [loadingPan, setLoadingPan] = useState(false);
  const [loadingCin, setLoadingCin] = useState(false);
  const [othersNotification, setOthersNotification] = useState(false);
  const openedRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const isCinDisabled = DISABLE_CIN_INPUT.has(entityType);
  const showVerifyBtn = SHOW_CIN_VERIFY.has(entityType); // CIN verify enabled?
  const isLLP = entityType === "LLP";

  useEffect(() => {
    setCompanyNamePAN("");
    setCompanyNameCIN("");
    setRegisteredAddress("");
    setPan("");
    setCin("");
    setPanStatus({ msg: "", type: "info" });
    setCinStatus({ msg: "", type: "info" });
    setCompanyType("");
    setCompanyTypeError("");
    setRegisteredAddressErr("");
  }, [entityType]);

  useEffect(() => {
    if (successNoti) setModal(false);
  }, [successNoti]);

  function normalizeString(str = "") {
    // Replace non-alphanumeric with space
    let result = str.replace(/[^a-zA-Z0-9]+/g, " ");

    // Trim and collapse multiple spaces
    result = result.trim().replace(/\s+/g, " ");

    // Uppercase
    result = result.toUpperCase();

    // Normalize company suffixes
    result = result.replace(/\bPRIVATE\b/g, "PVT");
    result = result.replace(/\bLIMITED\b/g, "LTD");

    // NEW: Collapse spaced-out initials (e.g., "V J" → "VJ")
    result = result.replace(/\b([A-Z])\s+([A-Z])\b/g, "$1$2");

    return result;
  }

  const handlePanChange = (e) => {
    const v = e.target.value.toUpperCase();
    if (v.length <= 10) {
      setPan(v);
      setPanVerified(false);
      setCompanyNamePAN("");
      panNameRef.current = "";
      if (panStatus.msg) setPanStatus({ msg: "", type: "info" });
    }
  };

  const handleCinChange = (e) => {
    const v = e.target.value.toUpperCase();
    if (v.length <= 21) {
      // ⬅️ limit to 7 chars
      setCin(v);
      setCinVerified(false);
      setCompanyNameCIN("");
      setRegisteredAddress("");
      if (cinStatus.msg) setCinStatus({ msg: "", type: "info" });
    }
  };

  const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
  // const handlePanVerify = async () => {
  //   const v = pan.trim().toUpperCase();
  //   if (!v) return setPanStatus({ msg: "Please enter a PAN.", type: "error" });
  //   if (!PAN_RE.test(v)) {
  //     return setPanStatus({
  //       msg: "PAN format should be ABCDE1234F.",
  //       type: "error",
  //     });
  //   }

  //   try {
  //     setLoadingPan(true);
  //     setPanStatus({ msg: "Verifying...", type: "info" });

  //     const res = await fetch(
  //       `${config.mock_api_endpoint}/basic-kyc/pan-verify`,
  //       {
  //         method: "POST",
  //         headers: {
  //           "Content-Type": "application/json",
  //           "X-API-Key": process.env.REACT_APP_CUPI_API_KEY,
  //         },
  //         body: JSON.stringify({ pan: v }),
  //       }
  //     );
  //     const data = await res.json();
  //     if (!res.ok) throw new Error(data?.message || "PAN verify failed");
  //     const category = data?.data?.provider_data?.category;
  //     if (category === "Individual") {
  //       if (entityType !== "Proprietorship") {
  //         return setPanStatus({
  //           msg: `An Individual's PAN is not allowed for ${entityType}.`,
  //           type: "error",
  //         });
  //       }
  //     } else {
  //       if (entityType === "Public Ltd" && category !== "Company") {
  //         return setPanStatus({
  //           type: "error",
  //           msg: "Enter the Company PAN.",
  //         });
  //       }
  //       if (entityType === "Private Limited (Pvt)" && category !== "Company") {
  //         return setPanStatus({
  //           type: "error",
  //           msg: "Enter the Company PAN.",
  //         });
  //       }
  //       if (
  //         entityType === "One Person Company (OPC)" &&
  //         category !== "Company"
  //       ) {
  //         return setPanStatus({
  //           type: "error",
  //           msg: "Enter the Company PAN.",
  //         });
  //       }
  //       if (entityType === "Section 8 Company" && category !== "Company") {
  //         return setPanStatus({
  //           type: "error",
  //           msg: "Enter the Company PAN.",
  //         });
  //       }
  //       if (entityType === "Proprietorship" && category !== "Individual") {
  //         return setPanStatus({
  //           type: "error",
  //           msg: "Enter the proprietor’s Individual PAN.",
  //         });
  //       }
  //       // Entity-specific rules
  //       if (entityType === "Partnership Firm" && category !== "Firm") {
  //         return setPanStatus({
  //           msg: "Please enter a Firm's PAN to continue.",
  //           type: "error",
  //         });
  //       }

  //       if (entityType === "LLP" && category !== "Firm") {
  //         return setPanStatus({
  //           msg: "Please enter a LLP's PAN to continue.",
  //           type: "error",
  //         });
  //       }
  //     }

  //     const isValidPan = data?.data?.provider_data?.status === "VALID";
  //     const fullName = (data?.data?.provider_data?.full_name || "").trim();
  //     if (!isValidPan) {
  //       return setPanStatus({
  //         msg: "Please enter valid PAN to continue.",
  //         type: "error",
  //       });
  //     }

  //     // ✅ record PAN company name & mark verified
  //     if (fullName) {
  //       setCompanyNamePAN(fullName);
  //       panNameRef.current = normalizeString(fullName); // ✅ use the same function
  //     }

  //     setPanVerified(true);
  //     setPanStatus({ msg: "PAN verified successfully.", type: "success" });

  //     if (cinVerified && companyNameCIN) {
  //       const { match, msg } = comparePanCin(
  //         panNameRef.current,
  //         companyNameCIN
  //       );
  //       if (!match) {
  //         setCinVerified(false);
  //         setCinStatus({ msg, type: "error" });
  //       }
  //     }
  //   } catch (err) {
  //     setPanVerified(false);
  //     setPanStatus({
  //       msg: "Verification failed. Please try again.",
  //       type: "error",
  //     });
  //   } finally {
  //     setLoadingPan(false);
  //   }
  // };

  const handlePanVerify = async () => {
    const v = pan.trim().toUpperCase();
    if (!v) return setPanStatus({ msg: "Please enter a PAN.", type: "error" });
    if (!PAN_RE.test(v)) {
      return setPanStatus({
        msg: "PAN format should be ABCDE1234F.",
        type: "error",
      });
    }

    try {
      setLoading(true);
      setLoadingPan(true);
      setPanStatus({ msg: "Verifying...", type: "info" });

      const res = await fetch(
        `${config.mock_api_endpoint}/basic-kyc/pan-verify`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": process.env.REACT_APP_CUPI_API_KEY,
          },
          body: JSON.stringify({ pan: v, entityType }),
        },
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "PAN verify failed");

      // Backend already validates entityType vs category.
      const providerData = data?.data?.provider_data;
      const fullName = (providerData?.full_name || "").trim();

      if (providerData?.status !== "VALID") {
        return setPanStatus({
          msg: "Please enter valid PAN to continue.",
          type: "error",
        });
      }

      if (fullName) {
        setCompanyNamePAN(fullName);
        panNameRef.current = normalizeString(fullName);
      }

      setPanVerified(true);
      setPanStatus({ msg: "PAN verified successfully.", type: "success" });

      // extra cross-check with CIN name
      if (cinVerified && companyNameCIN) {
        const { match, msg } = comparePanCin(
          panNameRef.current,
          companyNameCIN,
        );
        if (!match) {
          setCinVerified(false);
          setCinStatus({ msg, type: "error" });
        }
      }
    } catch (err) {
      setPanVerified(false);
      setPanStatus({
        msg: err?.message || "Verification failed. Please try again.",
        type: "error",
      });
    } finally {
      setLoading(false);
      setLoadingPan(false);
    }
  };

  const comparePanCin = (panName, cinName) => {
    console.log(
      "test---->",
      normalizeString(panName),
      normalizeString(cinName),
    );

    switch (true) {
      case !panName || !cinName:
        return { match: true, msg: "" }; // skip if missing

      case normalizeString(panName) === normalizeString(cinName):
        return { match: true, msg: "PAN and CIN match." };

      default:
        return { match: false, msg: "PAN and CIN details don't match." };
    }
  };

  const CIN_MIN_LEN = 6;
  const handleCinVerify = async () => {
    const v = cin.trim().toUpperCase();
    if (!v)
      return setCinStatus({ msg: "Please enter a CIN/LLPIN.", type: "error" });
    if (v.length < CIN_MIN_LEN) {
      return setCinStatus({ msg: "CIN/LLPIN looks too short.", type: "error" });
    }

    try {
      setLoading(true);
      setLoadingCin(true);
      setCinStatus({ msg: "Verifying...", type: "info" });

      const res = await fetch(
        `${config.mock_api_endpoint}/basic-kyc/cin-verify`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": process.env.REACT_APP_CUPI_API_KEY,
          },
          body: JSON.stringify({ cin: v }),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "CIN verify failed");

      const regAddr = (data?.data?.address || "").trim();
      const compName = (data?.data?.companyName || "").trim();
      const nameFromCin = normalizeString(compName);

      // 🧩 if PAN is verified & we have its name, require name-match
      if (panVerified && panNameRef.current && nameFromCin) {
        const { match, msg } = comparePanCin(panNameRef.current, compName);
        if (!match) {
          setCinVerified(false);
          setCompanyNameCIN("");
          return setCinStatus({ msg, type: "error" });
        }
      }

      if (regAddr) setRegisteredAddress(regAddr);
      if (compName) setCompanyNameCIN(compName);

      setCinVerified(true);
      setCinStatus({ msg: "CIN verified successfully.", type: "success" });
    } catch (err) {
      setCinVerified(false);
      setCinStatus({
        msg: err.message || "Verification failed. Please try again.",
        type: "error",
      });
    } finally {
      setLoading(false);
      setLoadingCin(false);
    }
  };

  const handleNext = async () => {
    if (panStatus.type === "error") setPanStatus({ msg: "", type: "info" });
    if (cinStatus.type === "error") setCinStatus({ msg: "", type: "info" });
    setCompanyTypeError("");
    setRegisteredAddressErr("");

    let valid = true;

    if (!pan.trim()) {
      setPanStatus({ msg: "PAN is required.", type: "error" });
      valid = false;
    }

    if (showVerifyBtn && !cin.trim()) {
      setCinStatus({ msg: "CIN/LLPIN is required.", type: "error" });
      valid = false;
    }

    if (entityType === "Others" && !companyType.trim()) {
      setCompanyTypeError("Company Type is required.");
      valid = false;
    }

    if (!registeredAddress.trim()) {
      setRegisteredAddressErr("Address is required.");
      valid = false;
    }

    if (!companyId) {
      setPanStatus({
        msg: "Missing user id. Please try again.",
        type: "error",
      });
      valid = false;
    }

    if (!valid) return;

    const resolvedCompanyName = (
      companyName ||
      companyNameCIN ||
      companyNamePAN ||
      ""
    ).trim();

    const payload = {
      name: name,
      email: email,
      phone_no: phoneNo,
      company_id: companyId,
      company_type: companyType || "NA",
      affiliate_code,
      entity_type: entityType,
      pan: pan.trim().toUpperCase(),
      cinOrLlpin:
        isCinDisabled || !cin.trim() ? null : cin.trim().toUpperCase(),
      company_name: resolvedCompanyName,
      address: registeredAddress.trim() || null,
    };

    try {
      setLoading(true);
      setSuccessNoti(true);
      const res = await fetch(
        `${config.mock_api_endpoint}/basic-kyc/non-gstin`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": process.env.REACT_APP_CUPI_API_KEY,
          },
          body: JSON.stringify(payload),
        },
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setSuccessNoti(false);
        const msg = data?.message || "Failed to save entity details.";
        if (/pan/i.test(msg)) setPanStatus({ msg, type: "error" });
        else if (/cin/i.test(msg)) setCinStatus({ msg, type: "error" });
        else setPanStatus({ msg, type: "error" });
        return;
      }

      // if (entityType !== "Others") {
      //   setSuccessNoti(true);
      // } else {
      // }
      if (entityType === "Others") {
        setOthersNotification(true);
      }
      setCompanyId("");
      setName("");
      setPhoneNo("");
      setEmail("");
      setAffiliate_code("")
      localStorage.removeItem("company_id");
      localStorage.removeItem("phoneNo");
      localStorage.removeItem("email");
      localStorage.removeItem("name");
      console.log(
        "redirectUrl----------------------------->",
        data.redirect_url,
      );

      if (data.redirect_url) {
        window.location.assign(
          //open in the same Page
          `${config.redirect_base_url}${data.redirect_url}`,
        );

        // window.open(
        //   //open in new Blank Page
        //   `${config.redirect_base_url}${data.redirect_url}`,
        //   "_blank", // Opens in a new tab (or window, depending on browser settings)
        // );
      }
    } catch (err) {
      setSuccessNoti(false);
      console.error("POST /basic-kyc/non-gstin failed:", err);
      setPanStatus({
        msg: "Could not save entity details. Please try again.",
        type: "error",
      });
    } finally {
      setLoading(false);
      setSuccessNoti(false);
    }
  };

  const helperDefault = isLLP ? (
    <span>LLPIN accepted as entered. No verification required.</span>
  ) : DISABLE_CIN_INPUT.has(entityType) ? (
    <span>CIN/LLPIN not required for {entityType}. Field disabled.</span>
  ) : (
    <span>
      Don&apos;t know your CIN?{" "}
      <Link
        component="button"
        underline="hover"
        sx={{ color: "#2348AB" }}
        onClick={() => setCinModalOpen(true)}
      >
        Click here
      </Link>
    </span>
  );

  if (successNoti) {
    return (
      <div className={`${styles.wrapper} container`}>
        <div
          style={{
            minHeight: "70vh",
            display: "grid",
            placeItems: "center",
            // padding: "16px",
          }}
        >
          <Notification />
        </div>
      </div>
    );
  }
  console.log(
    "cansubmit--->",
    panVerified,
    showVerifyBtn,
    cinVerified,
    companyName,
    companyNameCIN,
    companyNamePAN,
    agreed,
    companyId,
  );

  const canSubmit =
    panVerified &&
    (!showVerifyBtn || cinVerified) &&
    (companyName || companyNameCIN || companyNamePAN) &&
    agreed && // ⬅️ this is still false
    !!companyId;

  return (
    <>
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(255,255,255,0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
          }}
        >
          <CircularProgress size={60} sx={{ color: "#2348AB" }} />
        </div>
      )}
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Typography sx={{ mb: 1, fontWeight: 500 }}>
          Business Entity Type
        </Typography>

        <RadioGroup
          row
          value={entityType}
          onChange={(e) => setEntityType(e.target.value)}
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr 1fr",
              md: "repeat(4, minmax(0, 1fr))",
            },
            gap: 2,
            mb: 2,
          }}
        >
          {[
            "Private Limited (Pvt)",
            "Public Ltd",
            "LLP",
            "Partnership Firm",
            "Trust",
            "Proprietorship",
            "One Person Company (OPC)",
            "Section 8 Company",
            "Others",
          ].map((label) => (
            <FormControlLabel
              key={label}
              value={label}
              control={<Radio />}
              label={label}
              sx={{ m: 0 }}
            />
          ))}
        </RadioGroup>

        <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
          {/* PAN */}
          <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              label={
                <>
                  Business PAN<span style={{ color: "red" }}> *</span>
                </>
              }
              placeholder="ABCDE1234F"
              value={pan}
              onChange={handlePanChange}
              variant="outlined"
              sx={radius12}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      variant="contained"
                      onClick={handlePanVerify}
                      disabled={loadingPan}
                      sx={{
                        borderRadius: "10px",
                        textTransform: "none",
                        px: 2,
                        backgroundColor: "#2348ab",
                      }}
                    >
                      {loadingPan ? "..." : "Verify"}
                    </Button>
                  </InputAdornment>
                ),
              }}
              error={panStatus.type === "error"}
              helperText={panStatus.msg || " "}
              FormHelperTextProps={{
                sx:
                  panStatus.type === "success"
                    ? { color: "success.main" }
                    : undefined,
              }}
            />
          </Box>

          {/* CIN/LLPIN */}
          <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              // label="CIN/LLPIN"
              label={entityType === "LLP" ? "LLPIN" : "CIN"}
              placeholder={entityType === "LLP" ? "LLPIN" : "CIN"}
              value={cin}
              onChange={handleCinChange}
              disabled={isCinDisabled}
              variant="outlined"
              sx={radius12}
              InputProps={{
                endAdornment: showVerifyBtn ? (
                  <InputAdornment position="end">
                    <Button
                      variant="contained"
                      onClick={handleCinVerify}
                      disabled={loadingCin}
                      sx={{
                        borderRadius: "10px",
                        textTransform: "none",
                        px: 2,
                        backgroundColor: "#2348ab",
                      }}
                    >
                      {loadingCin ? "..." : "Verify"}
                    </Button>
                  </InputAdornment>
                ) : null,
              }}
              error={cinStatus.type === "error"}
              helperText={cinStatus.msg || helperDefault}
              FormHelperTextProps={{
                sx:
                  cinStatus.type === "success"
                    ? { color: "success.main" }
                    : undefined,
              }}
            />

            <Dialog
              open={cinModalOpen}
              onClose={() => setCinModalOpen(false)}
              maxWidth="sm"
              fullWidth
              aria-labelledby="cin-helper-title"
            >
              <DialogTitle sx={{ p: 2 }}>
                <Box className="d-flex justify-content-between align-items-center">
                  <Typography
                    id="cin-helper-title"
                    variant="h6"
                    className={styles.title}
                  >
                    Find your CIN/LLPIN
                  </Typography>
                  <img
                    src="/x-circle.svg"
                    onClick={() => setCinModalOpen(false)}
                    alt="close"
                  />
                </Box>
              </DialogTitle>

              <DialogContent dividers sx={{ p: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  1.&nbsp;&nbsp; Open MCA Portal →{" "}
                  <Link
                    href="https://www.mca.gov.in/content/mca/global/en/mca/fo-llp-services.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{
                      color: "#2348AB",
                      fontWeight: 600,
                      textDecoration: "underline",
                    }}
                  >
                    Click here
                  </Link>
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  2.&nbsp;&nbsp; Select “CIN/LLPIN”
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  3.&nbsp;&nbsp; Search by Name / Reg. Number
                </Typography>
                <Typography variant="body2">
                  4.&nbsp;&nbsp; Click Search → your CIN (for companies) or
                  LLPIN (for LLPs) will be displayed.
                </Typography>
              </DialogContent>
            </Dialog>
          </Box>
        </Box>

        {entityType === "Others" ? (
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label={
                <>
                  Company Type<span style={{ color: "red" }}> *</span>
                </>
              }
              value={companyType}
              onChange={(e) => setCompanyType(e.target.value)}
              variant="outlined"
              sx={radius12}
              error={!!companyTypeError} // highlight field in red
              helperText={companyTypeError || " "} // show error text
            />
          </Box>
        ) : null}

        {/* Company Name */}
        <Box sx={{ mt: 2, mb: 2 }}>
          <TextField
            fullWidth
            label="Company Name"
            value={companyName || companyNameCIN || companyNamePAN}
            onChange={(e) => setCompanyName(e.target.value)}
            variant="outlined"
            sx={radius12}
            disabled
          />
        </Box>

        {/* Registered Address */}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Registered Address"
            value={registeredAddress}
            onChange={(e) => setRegisteredAddress(e.target.value)}
            variant="outlined"
            sx={radius12}
            error={!!registeredAddressErr} // highlight field in red
            helperText={registeredAddressErr || " "} // show error text
          />
        </Box>

        <div className={`${styles.agreeWrap} mt-4`}>
          <input
            id="agree"
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            required
          />
          <label htmlFor="agree" className={styles.agreeText}>
            <b aria-hidden="true">*</b> I agree to the{" "}
          </label>
          <button
            type="button"
            className={styles.linkLike}
            onClick={() => setModal(true)}
            aria-haspopup="dialog"
            aria-controls="terms-dialog"
          >
            Terms &amp; Conditions
          </button>
          .
        </div>

        {/* ✅ Next Button */}
        <Box>
          <div className={styles.nextBtn}>
            <button
              className="px-5 pt-2 pb-2"
              onClick={() => navigate("/basic-kyc")}
            >
              Back
            </button>
            <button
              className="px-5 pt-2 pb-2"
              onClick={handleNext}
              disabled={!canSubmit}
            >
              Submit
            </button>
          </div>
        </Box>
      </Box>

      <Dialog
        open={modal}
        onClose={() => setModal(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="cin-helper-title"
        slotProps={{
          paper: { sx: { borderRadius: 3, overflow: "hidden" } },
        }}
      >
        <DialogTitle sx={{ p: 2 }}>
          <Box className="d-flex justify-content-between align-items-center">
            <Typography
              id="cin-helper-title"
              variant="h6"
              className={styles.modaltitle}
            >
              CUPI – Terms and Conditions
            </Typography>

            <img
              src="/x-circle.svg"
              onClick={() => setModal(false)}
              alt="close"
            />
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2 }}>
          <TermsAndConditions />
        </DialogContent>
      </Dialog>
      {/* Others Notification */}
      <Dialog
        open={othersNotification}
        onClose={() => setOthersNotification(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Details Submitted
          <IconButton
            aria-label="close"
            onClick={() => setOthersNotification(false)}
            edge="end"
            size="small"
          >
            {/* <CloseIcon /> */}
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            pb: 3, // add some bottom padding since we removed DialogActions
          }}
        >
          <Typography align="center" sx={{ color: "text.secondary" }}>
            Your Kyc is under Verification! We wil notify you through SMS!
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EntityBasedKyc;
